/**
 * Scenario fixtures — scripted incident timelines that serve double duty as
 * (a) seed/demo content and (b) golden regression oracles asserting which
 * insight cards should fire at each stage.
 *
 * Epic 0 ships the types + a validating loader; the context patch shape fills
 * out as the domain model grows in later epics.
 */

import type { IncidentStage } from './stages.js';
import { INCIDENT_STAGES } from './stages.js';

export interface ScenarioStep {
  /** Elapsed seconds from incident T0 at which this step occurs. */
  atElapsedSec: number;
  stage: IncidentStage;
  /** Free-form, partial context mutation applied at this step (typed later). */
  contextPatch?: Record<string, unknown>;
  /** Insight ids expected to be Open after this step (the golden assertion). */
  expectedInsightIds?: string[];
  /** Optional facilitator note shown in the scrubber. */
  note?: string;
}

export interface Scenario {
  id: string;
  title: string;
  /** Locality key referencing a seeded dataset (e.g. "bungendore"). */
  locality: string;
  /** Provenance label so synthetic/stand-in scenarios are never mistaken. */
  provenance: 'synthetic' | 'seeded' | 'mixed';
  summary: string;
  steps: ScenarioStep[];
}

export class ScenarioValidationError extends Error {}

/** Validate and normalise a raw scenario object (e.g. parsed JSON). */
export function parseScenario(raw: unknown): Scenario {
  if (typeof raw !== 'object' || raw === null) {
    throw new ScenarioValidationError('Scenario must be an object');
  }
  const s = raw as Record<string, unknown>;
  for (const key of ['id', 'title', 'locality', 'summary'] as const) {
    if (typeof s[key] !== 'string' || (s[key] as string).length === 0) {
      throw new ScenarioValidationError(`Scenario.${key} must be a non-empty string`);
    }
  }
  if (s.provenance !== 'synthetic' && s.provenance !== 'seeded' && s.provenance !== 'mixed') {
    throw new ScenarioValidationError('Scenario.provenance must be synthetic | seeded | mixed');
  }
  if (!Array.isArray(s.steps) || s.steps.length === 0) {
    throw new ScenarioValidationError('Scenario.steps must be a non-empty array');
  }

  const steps = s.steps.map((rawStep, i) => parseStep(rawStep, i));
  // Steps must be chronologically ordered.
  for (let i = 1; i < steps.length; i++) {
    if (steps[i]!.atElapsedSec < steps[i - 1]!.atElapsedSec) {
      throw new ScenarioValidationError(`Scenario.steps must be ordered by atElapsedSec (step ${i})`);
    }
  }

  return {
    id: s.id as string,
    title: s.title as string,
    locality: s.locality as string,
    provenance: s.provenance,
    summary: s.summary as string,
    steps,
  };
}

function parseStep(raw: unknown, index: number): ScenarioStep {
  if (typeof raw !== 'object' || raw === null) {
    throw new ScenarioValidationError(`Step ${index} must be an object`);
  }
  const st = raw as Record<string, unknown>;
  if (typeof st.atElapsedSec !== 'number' || st.atElapsedSec < 0) {
    throw new ScenarioValidationError(`Step ${index}.atElapsedSec must be a non-negative number`);
  }
  if (!INCIDENT_STAGES.includes(st.stage as IncidentStage)) {
    throw new ScenarioValidationError(`Step ${index}.stage is not a valid incident stage`);
  }
  return {
    atElapsedSec: st.atElapsedSec,
    stage: st.stage as IncidentStage,
    contextPatch: (st.contextPatch as Record<string, unknown>) ?? undefined,
    expectedInsightIds: Array.isArray(st.expectedInsightIds)
      ? (st.expectedInsightIds as string[])
      : undefined,
    note: typeof st.note === 'string' ? st.note : undefined,
  };
}

/** The step in effect at a given elapsed time (last step whose time has passed). */
export function stepAt(scenario: Scenario, elapsedSec: number): ScenarioStep {
  let current = scenario.steps[0]!;
  for (const step of scenario.steps) {
    if (step.atElapsedSec <= elapsedSec) current = step;
    else break;
  }
  return current;
}
